import { useEffect, useState } from "react";
import {
  ApiError,
  listMerchantApiKeys,
  rotateMerchantApiKey,
  type ApiKeyInfo,
  type RotatedApiKey,
} from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { formatDate, formatRelative } from "@/lib/format";
import { PageHeader } from "@/components/PageHeader";
import { Badge, statusColor } from "@/components/DataTable";
import { Modal } from "@/components/Modal";
import { RotateCcw, AlertTriangle, KeyRound, Copy, CheckCircle2, Eye, EyeOff } from "lucide-react";

export function MerchantApiKeysPage() {
  const auth = useAuth();
  const me = auth.status === "authenticated" ? auth.user : null;
  // Integration team (IT) owns API keys lifecycle: read + rotate. Cf ADR-0016.
  const canRotate = me?.role === "it" || me?.role === "admin";

  const [keys, setKeys] = useState<ApiKeyInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [rotating, setRotating] = useState(false);
  const [rotated, setRotated] = useState<RotatedApiKey | null>(null);
  const [copied, setCopied] = useState<string | null>(null);
  const [showSecret, setShowSecret] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await listMerchantApiKeys();
      setKeys(r.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const copy = async (k: string, v: string) => {
    await navigator.clipboard.writeText(v);
    setCopied(k);
    setTimeout(() => setCopied(null), 2000);
  };

  const onRotate = async () => {
    setRotating(true);
    try {
      const r = await rotateMerchantApiKey();
      setRotated(r);
      setShowSecret(false);
      setConfirmOpen(false);
      await load();
    } catch (err) {
      alert(err instanceof ApiError ? err.detail || err.title : "Erreur rotation");
    } finally {
      setRotating(false);
    }
  };

  const active = keys.filter((k) => k.status === "active");
  const old = keys.filter((k) => k.status !== "active");

  return (
    <div>
      <PageHeader
        title="Clés API"
        breadcrumbs={[{ label: "Mon espace" }, { label: "Clés API" }]}
        actions={
          canRotate && (
            <button onClick={() => setConfirmOpen(true)} className="btn btn-light-danger btn-sm">
              <RotateCcw size={14} />
              Régénérer les clés
            </button>
          )
        }
      />

      {loading ? (
        <div className="card card-body text-[#7e8299]">Chargement…</div>
      ) : (
        <>
          {active.length > 0 && (
            <div className="grid grid-cols-1 gap-5 mb-6">
              {active.map((k) => (
                <div key={k.id} className="card">
                  <div className="card-header">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-brand-50 text-brand-700 flex items-center justify-center">
                        <KeyRound size={18} />
                      </div>
                      <div>
                        <div className="card-title">Clé API active</div>
                        <div className="card-subtitle">Créée le {formatDate(k.created_at)}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge color={k.livemode ? "info" : "light"}>{k.livemode ? "Live" : "Test"}</Badge>
                      <Badge color={statusColor(k.status)}>{k.status}</Badge>
                    </div>
                  </div>
                  <div className="card-body space-y-4">
                    <div>
                      <div className="form-label">Clé publique (API Key)</div>
                      <div className="flex items-stretch gap-2">
                        <code className="flex-1 px-3 py-2.5 bg-[#f5f8fa] rounded-lg text-xs font-mono break-all">{k.api_key}</code>
                        <button onClick={() => copy(`api_${k.id}`, k.api_key)} className="btn btn-light-primary btn-sm shrink-0">
                          {copied === `api_${k.id}` ? <CheckCircle2 size={14} /> : <Copy size={14} />}
                        </button>
                      </div>
                      <div className="form-hint">Cette clé est publique. Vous la mettez dans le header <code className="font-mono">X-API-Key</code> de vos requêtes.</div>
                    </div>

                    <div>
                      <div className="form-label">Secret API</div>
                      <div className="px-3 py-2.5 bg-[#f5f8fa] rounded-lg text-xs font-mono text-[#7e8299]">
                        sk_{k.livemode ? "live" : "test"}_…{k.api_secret_last4}
                      </div>
                      <div className="form-hint">
                        Le secret n'est pas récupérable. <strong>Régénérez la clé</strong> pour obtenir un nouveau secret.
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3 pt-3 border-t border-[#eff2f5]">
                      <div>
                        <div className="text-xs uppercase tracking-wider text-[#7e8299] font-bold mb-0.5">Dernière utilisation</div>
                        <div className="text-sm text-[#181c32]">{k.last_used_at ? formatRelative(k.last_used_at) : "Jamais"}</div>
                      </div>
                      <div>
                        <div className="text-xs uppercase tracking-wider text-[#7e8299] font-bold mb-0.5">ID credential</div>
                        <div className="text-xs font-mono text-[#7e8299] truncate">{k.id}</div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {old.length > 0 && (
            <div className="card">
              <div className="card-header">
                <div className="card-title">Clés révoquées ({old.length})</div>
              </div>
              <div className="card-body space-y-2">
                {old.map((k) => (
                  <div key={k.id} className="flex items-center justify-between p-3 rounded-lg bg-[#fcfcfd] border border-[#eff2f5]">
                    <div>
                      <code className="font-mono text-xs text-[#7e8299]">{k.api_key}</code>
                      <div className="text-xs text-[#7e8299] mt-0.5">
                        Révoquée {k.revoked_at ? formatRelative(k.revoked_at) : "—"}
                      </div>
                    </div>
                    <Badge color={statusColor(k.status)}>{k.status}</Badge>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* Confirm modal */}
      <Modal
        open={confirmOpen}
        onClose={() => !rotating && setConfirmOpen(false)}
        title="Régénérer les clés API"
        size="md"
        closeOnOverlay={!rotating}
        footer={
          <>
            <button onClick={() => setConfirmOpen(false)} disabled={rotating} className="btn btn-light btn-sm">
              Annuler
            </button>
            <button onClick={onRotate} disabled={rotating} className="btn btn-danger btn-sm">
              <RotateCcw size={14} />
              {rotating ? "Régénération…" : "Confirmer la rotation"}
            </button>
          </>
        }
      >
        <div className="space-y-4">
          <div className="rounded-lg bg-danger-50 border-l-4 border-danger-500 px-4 py-3 flex items-start gap-3">
            <AlertTriangle size={20} className="text-danger-700 shrink-0 mt-0.5" />
            <div>
              <div className="font-bold text-danger-700 text-sm">Cette action est irréversible</div>
              <div className="text-xs text-danger-700 mt-1 leading-relaxed">
                Les clés actuelles seront <strong>révoquées immédiatement</strong>. Tout VRS ou intégration utilisant l'ancien
                secret cessera de fonctionner jusqu'à mise à jour avec le nouveau.
              </div>
            </div>
          </div>
          <p className="text-sm text-[#3f4254] leading-relaxed">
            Préparez-vous à mettre à jour vos systèmes immédiatement avec le nouveau secret qui sera affiché une seule fois.
          </p>
        </div>
      </Modal>

      {/* Rotated result modal */}
      <Modal
        open={rotated !== null}
        onClose={() => setRotated(null)}
        title="Nouvelles clés générées"
        subtitle="Affichées une seule fois — copiez-les maintenant"
        size="lg"
        closeOnOverlay={false}
        footer={
          <button onClick={() => setRotated(null)} className="btn btn-primary btn-sm">
            J'ai sauvegardé les clés
          </button>
        }
      >
        {rotated && (
          <div className="space-y-4">
            <div className="rounded-lg bg-warning-50 border-l-4 border-warning-500 px-4 py-3 flex items-start gap-3">
              <AlertTriangle size={20} className="text-warning-700 shrink-0 mt-0.5" />
              <div className="text-xs text-warning-700">{rotated.warning}</div>
            </div>

            <div>
              <div className="form-label">API Key (publique)</div>
              <div className="flex items-stretch gap-2">
                <code className="flex-1 px-3 py-2.5 bg-[#f5f8fa] rounded-lg text-xs font-mono break-all">{rotated.api_key}</code>
                <button onClick={() => copy("nk", rotated.api_key)} className="btn btn-light-primary btn-sm shrink-0">
                  {copied === "nk" ? <CheckCircle2 size={14} /> : <Copy size={14} />}
                </button>
              </div>
            </div>

            <div>
              <div className="form-label flex items-center justify-between">
                <span>API Secret</span>
                <button onClick={() => setShowSecret((s) => !s)} className="text-xs text-brand-700 inline-flex items-center gap-1">
                  {showSecret ? <EyeOff size={12} /> : <Eye size={12} />}
                  {showSecret ? "Masquer" : "Afficher"}
                </button>
              </div>
              <div className="flex items-stretch gap-2">
                <code className="flex-1 px-3 py-2.5 bg-[#f5f8fa] rounded-lg text-xs font-mono break-all">
                  {showSecret ? rotated.api_secret : "•".repeat(rotated.api_secret.length)}
                </code>
                <button onClick={() => copy("ns", rotated.api_secret)} className="btn btn-light-primary btn-sm shrink-0">
                  {copied === "ns" ? <CheckCircle2 size={14} /> : <Copy size={14} />}
                </button>
              </div>
            </div>

            <div>
              <div className="form-label">HMAC Callback Secret</div>
              <div className="flex items-stretch gap-2">
                <code className="flex-1 px-3 py-2.5 bg-[#f5f8fa] rounded-lg text-xs font-mono break-all">{rotated.hmac_callback_secret}</code>
                <button onClick={() => copy("hm", rotated.hmac_callback_secret)} className="btn btn-light-primary btn-sm shrink-0">
                  {copied === "hm" ? <CheckCircle2 size={14} /> : <Copy size={14} />}
                </button>
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
